import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { PlanLink } from '@app/router/constants';
import { AemFeature } from '@app/components/AdobeExperienceFragments';
import { PageAlert } from '@app/components/Alerts/PageAlert';
import AlertSectionLayout from '@app/components/AlertSectionLayout';
import { CommonQuestions } from '@app/components/CommonQuestions';
import { MobileProfileButton } from '@app/components/MobileProfileButton';
import { LoadingModal } from '@app/components/Modal';
import { PageLayout } from '@app/components/PageLayout';
import { useNavigate } from '@app/hooks';
import { selectAddOns } from '@app/redux/addOns';
import { AlertType, DisplayLocation, selectAlerts } from '@app/redux/alerts';
import { selectUser } from '@app/redux/auth';
import { getHasPendingTransition } from '@app/redux/changePlan';
import { selectConfig } from '@app/redux/config';
import { selectLocale } from '@app/redux/locale';
import { selectCurrentUsage, selectPlanCharacteristics } from '@app/redux/plan';
import { selectUserInfo } from '@app/redux/userInfo';
import { selectUtil, setUsagePageStatistics, setUsageSummaryPageStatistics } from '@app/redux/utils';
import { getWorkOrders, selectWorkOrders } from '@app/redux/workOrders';
import { checkAlertDismissed, isVoiceOnlyPlan } from '@app/utils';
import { useCommonQuestionsFeature } from '@app/utils/useCommonQuestionsFeature';

import { AccountType, GetCurrentAddOnsPayload, Locale, Platform } from '@mfe/shared/schema-types';

import { BuyMore, ModalContext, useDialogType } from '../AddDataBoost';
import { AemAd } from './aem-ad';
import { ArticleSection } from './article-section';
import { DataUsageLoading, UsageHistory } from './index';
import StreamCard from './stream-card';
import UsageSummary from './usage-summary';
import { ADDON_KIND } from '../../constants';

import styles from './usage.module.scss';

const Usage = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const [dialogType, dialogTypeHandlers] = useDialogType();
  const { resetDialogType, setBuyMoreDialog } = dialogTypeHandlers;

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);
  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);
  const { workOrders } = useSelector(selectWorkOrders);
  const {
    user: {
      auth: { isAuthenticated },
    },
  } = useSelector(selectUser);
  const { platform } = useSelector(selectConfig);
  const {
    pageStats: {
      pages: {
        usage: { lastUpdatedTime: lastUpdatedTimeUsage },
        usageSummary: { lastUpdatedTime: lastUpdatedTimeUsageSummary },
      },
    },
  } = useSelector(selectUtil);
  const { currentAddOns } = useSelector(selectAddOns);
  const {
    characteristics: { id: productTypeId },
  } = useSelector(selectPlanCharacteristics);

  useEffect(() => {
    if (!lastUpdatedTimeUsageSummary && isAuthenticated) dispatch(setUsageSummaryPageStatistics());
  }, [dispatch, lastUpdatedTimeUsageSummary, isAuthenticated]);

  useEffect(() => {
    if (!lastUpdatedTimeUsage && isAuthenticated) dispatch(setUsagePageStatistics());
  }, [dispatch, lastUpdatedTimeUsage, isAuthenticated]);

  useEffect(() => {
    dispatch(getHasPendingTransition());
    if (workOrders === null) dispatch(getWorkOrders());
  }, []);

  const hasStreamOnActive = currentAddOns?.find(
    (addOn: GetCurrentAddOnsPayload) => addOn.kind === ADDON_KIND.CONTENT_ADD_ON
  );

  const hasVoiceOnlyPlan = isVoiceOnlyPlan(productTypeId);

  const isUS = userLocale === Locale.EnUs;
  const isResidential = isUS && accountType === AccountType.Residential;

  const mobileAppModal =
    platform !== Platform.Web && dialogType !== null ? (
      <React.Suspense fallback={<LoadingModal isOpen={true} handleClose={() => resetDialogType()} />}>
        <BuyMore />
      </React.Suspense>
    ) : null;

  const { alerts: allAlerts, loading: alertsLoading } = useSelector(selectAlerts);

  const alerts = allAlerts.filter(
    (alert) => alert.displayLocation === DisplayLocation.GLOBAL || alert.type === AlertType.AllDataUsedNextGen
  );

  const navigateToPage = (link: string) => {
    goTo(link);
  };
  const navigateToChangePlan = () => {
    goTo(`${PlanLink.Plan}/${PlanLink.ChoosePlanFilter}`);
  };

  const overviewCommonQuestionsFeature = useCommonQuestionsFeature({
    fiber: AemFeature.USAGE_COMMON_QUESTIONS_FIBER,
    default: AemFeature.USAGE_COMMON_QUESTIONS,
  });

  return (
    <ModalContext.Provider value={{ dialogType, dialogTypeHandlers }}>
      {mobileAppModal ?? (
        <>
          <MobileProfileButton />
          <PageLayout>
            {!!alerts.length && (
              <AlertSectionLayout>
                {alerts
                  .filter((alert) => !checkAlertDismissed(alert.type))
                  .map((alert, i) => (
                    <PageAlert key={i} alert={alert} loading={alertsLoading} setBuyMoreDialog={setBuyMoreDialog} />
                  ))}
              </AlertSectionLayout>
            )}
            <div data-cy={'usage-home-loaded'} className={styles['usageGrid']}>
              <UsageSummary navigateToPage={navigateToPage} navigateToChangePlan={navigateToChangePlan} />

              <div className={styles['rightColumn']}>
                {!hasVoiceOnlyPlan && <UsageHistoryWidget />}
                {isResidential && <AemAd />}
                <ArticleSection />
                {hasStreamOnActive && <StreamCard />}
              </div>

              <div className={styles['commonQuestions']}>
                <CommonQuestions feature={overviewCommonQuestionsFeature} />
              </div>
            </div>
          </PageLayout>
        </>
      )}
    </ModalContext.Provider>
  );
};

const UsageHistoryWidget = () => {
  const userInfo = useSelector(selectUser);
  const { loading: usageLoading } = useSelector(selectCurrentUsage);
  const { t } = useTranslation();

  const isDataUsageLoaded = userInfo.loading === false;

  const showChartLoading = usageLoading || !isDataUsageLoaded;

  if (showChartLoading) {
    return <DataUsageLoading chartTitle={t('UsageHistory:chartTitle')} />;
  }

  return (
    <div className={`${styles['usageChartContainer']}`}>
      <UsageHistory />
    </div>
  );
};

export default Usage;
